import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Plus, Pencil, Trash2, Eye, EyeOff, Download, Wand2, Loader2, AlertCircle, CheckCircle2, FileText, Upload } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

interface ProductCategory {
  id: string;
  name: string;
  slug: string;
  icon: string;
  color: string;
  is_active: boolean;
}

interface DigitalProduct {
  id: string;
  title: string;
  description: string;
  category: string;
  file_url: string | null;
  preview_image: string | null;
  file_size: string | null;
  file_format: string | null;
  download_count: number;
  tags: string[] | null;
  is_active: boolean;
  created_at: string;
  pdf_file_path: string | null;
  content_type: string;
}

interface ProductCategory {
  id: string;
  name: string;
  slug: string;
  icon: string;
  color: string;
  is_active: boolean;
}

const DigitalProducts = () => {
  const [products, setProducts] = useState<DigitalProduct[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<DigitalProduct | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [generatingImage, setGeneratingImage] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [downloadUrlDuplicateError, setDownloadUrlDuplicateError] = useState<string | null>(null);
  const [isCheckingDownloadDuplicate, setIsCheckingDownloadDuplicate] = useState(false);
  const [downloadUrlValidated, setDownloadUrlValidated] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    file_url: "",
    preview_image: "",
    file_size: "",
    file_format: "",
    tags: "",
    is_active: true,
    content_type: "download" as "download" | "viewable",
    pdf_file_path: "",
  });
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const pdfInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    const normalizedUrl = formData.file_url.trim();

    if (!normalizedUrl) {
      setDownloadUrlDuplicateError(null);
      setIsCheckingDownloadDuplicate(false);
      setDownloadUrlValidated(false);
      return;
    }

    setDownloadUrlValidated(false);
    setIsCheckingDownloadDuplicate(true);

    const timeoutId = setTimeout(async () => {
      try {
        const { data, error } = await supabase
          .from("digital_products")
          .select("id, title")
          .eq("file_url", normalizedUrl);

        if (error) {
          console.error("Error checking duplicate Download URL (live):", error);
          setDownloadUrlDuplicateError(null);
          setDownloadUrlValidated(false);
          return;
        }

        const duplicates = (data || []).filter(
          (product) => product.id !== editingProduct?.id
        );

        if (duplicates.length > 0) {
          const duplicateTitle = duplicates[0]?.title || "another product";
          setDownloadUrlDuplicateError(
            `This Download URL is already used by "${duplicateTitle}".`
          );
          setDownloadUrlValidated(false);
        } else {
          setDownloadUrlDuplicateError(null);
          setDownloadUrlValidated(true);
        }
      } finally {
        setIsCheckingDownloadDuplicate(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [formData.file_url, editingProduct?.id]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("product_categories")
        .select("*")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from("digital_products")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast({
        title: "Error",
        description: "Failed to load products",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // For download products, file_url is required. For viewable PDFs, it's optional
    const requiresFileUrl = formData.content_type === "download";
    
    if (!formData.title || !formData.description || !formData.category) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    if (requiresFileUrl && !formData.file_url.trim()) {
      toast({
        title: "Validation Error",
        description: "Download URL is required for downloadable products",
        variant: "destructive",
      });
      return;
    }

    if (downloadUrlDuplicateError) {
      toast({
        title: "Duplicate Download URL",
        description: "Please fix the duplicate Download URL before saving.",
        variant: "destructive",
      });
      return;
    }

    if (isCheckingDownloadDuplicate) {
      toast({
        title: "Please wait",
        description: "Still checking for duplicate Download URL. Try again in a moment.",
        variant: "destructive",
      });
      return;
    }

    const normalizedFileUrl = formData.file_url.trim() || null;

    try {
      // Check for duplicate Download URL before saving (only if URL is provided)
      if (normalizedFileUrl) {
        const { data: existingProducts, error: duplicateCheckError } = await supabase
          .from("digital_products")
          .select("id, title")
          .eq("file_url", normalizedFileUrl);

        if (duplicateCheckError) {
          console.error("Error checking duplicate Download URL:", duplicateCheckError);
          toast({
            title: "Error",
            description: "Could not verify Download URL uniqueness. Please try again.",
            variant: "destructive",
          });
          return;
        }

        const duplicates = (existingProducts || []).filter((product) => product.id !== editingProduct?.id);

        if (duplicates.length > 0) {
          const duplicateTitle = duplicates[0]?.title || "another product";
          toast({
            title: "Duplicate Download URL",
            description: `This Download URL is already used by "${duplicateTitle}". Please use a different link.`,
            variant: "destructive",
          });
          return;
        }
      }

      const productData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        file_url: normalizedFileUrl,
        preview_image: formData.preview_image || null,
        file_size: formData.file_size || null,
        file_format: formData.file_format || null,
        tags: formData.tags ? formData.tags.split(",").map((t) => t.trim()) : null,
        is_active: formData.is_active,
        content_type: formData.content_type,
        pdf_file_path: formData.pdf_file_path || null,
      };

      if (editingProduct) {
        const { error } = await supabase
          .from("digital_products")
          .update(productData)
          .eq("id", editingProduct.id);

        if (error) throw error;
        toast({ title: "Success", description: "Product updated successfully" });
      } else {
        const { error } = await supabase.from("digital_products").insert(productData);

        if (error) throw error;
        toast({ title: "Success", description: "Product created successfully" });
      }

      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error("Error saving product:", error);
      toast({
        title: "Error",
        description: "Failed to save product",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (product: DigitalProduct) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      description: product.description,
      category: product.category,
      file_url: product.file_url || "",
      preview_image: product.preview_image || "",
      file_size: product.file_size || "",
      file_format: product.file_format || "",
      tags: product.tags?.join(", ") || "",
      is_active: product.is_active,
      content_type: (product.content_type as "download" | "viewable") || "download",
      pdf_file_path: product.pdf_file_path || "",
    });
    setDialogOpen(true);
  };

  const handlePdfUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast({
        title: "Invalid file type",
        description: "Please upload a PDF file.",
        variant: "destructive",
      });
      event.target.value = "";
      return;
    }

    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: "PDF must be smaller than 50MB.",
        variant: "destructive",
      });
      event.target.value = "";
      return;
    }

    setUploadingPdf(true);
    try {
      const filePath = `pdfs/${crypto.randomUUID()}.pdf`;

      const { error: uploadError } = await supabase.storage
        .from("product-pdfs")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) throw uploadError;

      setFormData((prev) => ({
        ...prev,
        pdf_file_path: filePath,
        file_format: "PDF",
        content_type: "viewable",
        file_size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
      }));

      toast({
        title: "PDF Uploaded",
        description: "PDF file has been uploaded. Users can now view it in the browser.",
      });
    } catch (error: any) {
      console.error("Error uploading PDF:", error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload PDF.",
        variant: "destructive",
      });
    } finally {
      setUploadingPdf(false);
      event.target.value = "";
    }
  };

  const handleDelete = async () => {
    if (!productToDelete) return;

    try {
      const { error } = await supabase
        .from("digital_products")
        .delete()
        .eq("id", productToDelete);

      if (error) throw error;

      toast({ title: "Success", description: "Product deleted successfully" });
      fetchProducts();
    } catch (error) {
      console.error("Error deleting product:", error);
      toast({
        title: "Error",
        description: "Failed to delete product",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  const toggleProductVisibility = async (productId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from("digital_products")
        .update({ is_active: !currentStatus })
        .eq("id", productId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Product ${!currentStatus ? "activated" : "deactivated"}`,
      });
      fetchProducts();
    } catch (error) {
      console.error("Error toggling visibility:", error);
      toast({
        title: "Error",
        description: "Failed to update product visibility",
        variant: "destructive",
      });
    }
  };

  const handleGeneratePreview = async () => {
    if (!formData.title) {
      toast({
        title: "Title Required",
        description: "Please enter a product title first",
        variant: "destructive",
      });
      return;
    }

    setGeneratingImage(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-product-preview', {
        body: { 
          title: formData.title,
          category: formData.category 
        }
      });

      if (error) {
        console.error('Error generating preview:', error, data);
        const errorMessage = (data as any)?.error || (error as any)?.message || "Failed to generate preview image";
        toast({
          title: "Generation Failed",
          description: errorMessage,
          variant: "destructive",
        });
        return;
      }

      if (data?.imageUrl) {
        setFormData(prev => ({ ...prev, preview_image: data.imageUrl }));
        toast({
          title: "Preview Generated!",
          description: "AI-generated preview image is ready",
        });
      }
    } catch (error: any) {
      console.error('Unexpected error generating preview:', error);
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate preview image",
        variant: "destructive",
      });
    } finally {
      setGeneratingImage(false);
    }
  };

  const handlePreviewImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file (JPG, PNG, etc.)",
        variant: "destructive",
      });
      event.target.value = "";
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: "Image must be smaller than 5MB",
        variant: "destructive",
      });
      event.target.value = "";
      return;
    }

    setUploadingImage(true);
    try {
      const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const filePath = `previews/${crypto.randomUUID()}.${extension}`;

      const { error: uploadError } = await supabase.storage
        .from("digital-products")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage
        .from("digital-products")
        .getPublicUrl(filePath);

      if (!publicUrlData?.publicUrl) {
        throw new Error("Unable to get public URL for uploaded image");
      }

      setFormData((prev) => ({ ...prev, preview_image: publicUrlData.publicUrl }));
      toast({
        title: "Image uploaded",
        description: "Preview image has been uploaded successfully.",
      });
    } catch (error: any) {
      console.error("Error uploading preview image:", error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setUploadingImage(false);
      event.target.value = "";
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      category: "",
      file_url: "",
      preview_image: "",
      file_size: "",
      file_format: "",
      tags: "",
      is_active: true,
      content_type: "download",
      pdf_file_path: "",
    });
    setEditingProduct(null);
    setDownloadUrlDuplicateError(null);
    setIsCheckingDownloadDuplicate(false);
    setDownloadUrlValidated(false);
  };

  const filteredProducts = products.filter(
    (p) =>
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalDownloads = products.reduce((sum, p) => sum + p.download_count, 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Digital Products</h1>
            <p className="text-muted-foreground">Manage your premium digital resource library</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="gap-2">
                <Plus className="w-4 h-4" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProduct ? "Edit Product" : "Add New Product"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Premium Icon Pack Pro"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the product..."
                    rows={4}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="category">Category *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.slug}>
                            {cat.icon} {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="file_format">File Format</Label>
                    <Input
                      id="file_format"
                      value={formData.file_format}
                      onChange={(e) => setFormData({ ...formData, file_format: e.target.value })}
                      placeholder="PNG, SVG, PDF"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="file_url">
                    Download URL {formData.content_type === "download" ? "*" : "(Optional)"}
                  </Label>
                  <Input
                    id="file_url"
                    value={formData.file_url}
                    onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                    placeholder="https://..."
                    required={formData.content_type === "download"}
                    className={downloadUrlDuplicateError ? "border-destructive" : ""}
                  />
                  <p className="text-xs text-muted-foreground">
                    {formData.content_type === "viewable" 
                      ? "Optional for viewable PDFs - users view in browser"
                      : "Required for downloadable products"}
                  </p>
                  {isCheckingDownloadDuplicate && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Checking for duplicate Download URL...</span>
                    </div>
                  )}
                  {downloadUrlValidated && !downloadUrlDuplicateError && !isCheckingDownloadDuplicate && formData.file_url.trim() && (
                    <div className="flex items-center gap-2 text-sm text-foreground">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>Download URL is unique - ready to use!</span>
                    </div>
                  )}
                  {downloadUrlDuplicateError && (
                    <Alert variant="destructive">
                      <AlertDescription className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 mt-0.5" />
                        <span>{downloadUrlDuplicateError}</span>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preview_image">Preview Image</Label>
                  <div className="flex gap-2">
                    <Input
                      id="preview_image"
                      value={formData.preview_image}
                      onChange={(e) => setFormData({ ...formData, preview_image: e.target.value })}
                      placeholder="Paste image URL or use Generate / Upload"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleGeneratePreview}
                      disabled={!formData.title || generatingImage}
                      className="shrink-0"
                    >
                      {generatingImage ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Wand2 className="h-4 w-4" />
                      )}
                      <span className="ml-2">Generate</span>
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handlePreviewImageUpload}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Uploading...
                        </>
                      ) : (
                        "Upload Image"
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      JPG or PNG, up to 5MB.
                    </p>
                  </div>
                  {formData.preview_image && (
                    <div className="mt-2">
                      <img
                        src={formData.preview_image}
                        alt="Product preview image"
                        className="w-full h-32 object-cover rounded border"
                      />
                    </div>
                  )}
                </div>

                {/* PDF Upload Section */}
                <div className="space-y-2 p-4 border border-dashed border-border rounded-lg bg-muted/30">
                  <Label className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    PDF for In-Browser Viewing
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Upload a PDF to let users view it directly in the browser with clickable links.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      ref={pdfInputRef}
                      type="file"
                      accept=".pdf,application/pdf"
                      className="hidden"
                      onChange={handlePdfUpload}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => pdfInputRef.current?.click()}
                      disabled={uploadingPdf}
                    >
                      {uploadingPdf ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload PDF
                        </>
                      )}
                    </Button>
                    {formData.pdf_file_path && (
                      <div className="flex items-center gap-2 text-sm text-green-600">
                        <CheckCircle2 className="h-4 w-4" />
                        <span>PDF uploaded - viewable in browser</span>
                      </div>
                    )}
                  </div>
                  {formData.pdf_file_path && (
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="secondary" className="gap-1">
                        <Eye className="w-3 h-3" />
                        Viewable
                      </Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData(prev => ({ ...prev, pdf_file_path: "", content_type: "download" }))}
                        className="text-destructive hover:text-destructive"
                      >
                        Remove PDF
                      </Button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="file_size">File Size</Label>
                    <Input
                      id="file_size"
                      value={formData.file_size}
                      onChange={(e) => setFormData({ ...formData, file_size: e.target.value })}
                      placeholder="2.5 MB"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tags">Tags (comma-separated)</Label>
                    <Input
                      id="tags"
                      value={formData.tags}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                      placeholder="design, icons, ui"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active">Active (visible to public)</Label>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1">
                    {editingProduct ? "Update Product" : "Create Product"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setDialogOpen(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Products
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{products.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Active Products
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {products.filter((p) => p.is_active).length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Downloads
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{totalDownloads.toLocaleString()}</div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="flex gap-4">
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-sm"
          />
        </div>

        {/* Products Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Format</TableHead>
                  <TableHead>Downloads</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      No products found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProducts.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{product.title}</div>
                          <div className="text-sm text-muted-foreground line-clamp-1">
                            {product.description}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {categories.find((c) => c.slug === product.category)?.icon}{" "}
                          {categories.find((c) => c.slug === product.category)?.name}
                        </Badge>
                      </TableCell>
                      <TableCell>{product.file_format || "—"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Download className="w-3 h-3" />
                          {product.download_count}
                        </div>
                      </TableCell>
                      <TableCell>
                        {product.is_active ? (
                          <Badge variant="default" className="gap-1">
                            <Eye className="w-3 h-3" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="gap-1">
                            <EyeOff className="w-3 h-3" />
                            Hidden
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleProductVisibility(product.id, product.is_active)}
                          >
                            {product.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(product)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setProductToDelete(product.id);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this product? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default DigitalProducts;
